import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
class TgmMoreDropdown {
  static nextId = 0;

  constructor(host, resetThemeMenu) {
    this.host = host;
    this.resetThemeMenu = resetThemeMenu;
    this.abort = new AbortController();
    this.isOpen = false;
    this.popup = document.createElement('div');
    this.popup.id = `tgm-more-${++TgmMoreDropdown.nextId}`;
    this.popup.className = 'tgm-more-dropdown';
    this.popup.hidden = true;
    this.popup.setAttribute('popover', 'manual');
    document.body.append(this.popup);
    TgmMoreDropdown.installStyles();
    const signal = this.abort.signal;
    host.addEventListener('click', this.onHostClick, { capture: true, signal });
    host.addEventListener('keydown', this.onTriggerKey, { capture: true, signal });
    document.addEventListener('pointerdown', this.onOutsidePointer, { capture: true, signal });
    document.addEventListener('focusin', this.onFocus, { signal });
    document.addEventListener('keydown', this.onPopupKey, { signal });
    this.popup.addEventListener('click', this.onLinkClick, { signal });
    window.addEventListener('resize', this.onResize, { signal });
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true, signal });
    this.observer = new MutationObserver(() => this.syncButton());
    this.observer.observe(host, { childList: true, subtree: true });
    this.syncButton();
  }

  get button() {
    return this.host.querySelector('[slot="more"] button') ??
      this.host.refs.overflowMenu?.shadowRoot?.querySelector('slot[name="more"] button');
  }

  isMoreEvent(event) {
    const button = this.button;
    return event.composedPath().some(node => node === button ||
      (node instanceof Element && node.matches('[slot="more"], slot[name="more"]')));
  }

  syncButton() {
    const button = this.button;
    if (!button) return;
    if (this.currentButton && this.currentButton !== button) this.close();
    this.currentButton = button;
    button.type = 'button';
    button.removeAttribute('role');
    button.setAttribute('aria-controls', this.popup.id);
    button.setAttribute('aria-expanded', String(this.isOpen));
    button.setAttribute('data-tgm-more-trigger', '');
    if (!button.querySelector('.tgm-more-chevron')) {
      const chevron = document.createElement('span');
      chevron.className = 'tgm-more-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      button.append(chevron);
    }
  }

  onHostClick = event => {
    if (!this.isMoreEvent(event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.isOpen) this.close();
    else this.open();
  };

  onTriggerKey = event => {
    if (!this.isMoreEvent(event)) return;
    if (event.key === 'Tab' && this.isOpen) {
      if (event.shiftKey) { this.close(); return; }
      event.preventDefault();
      event.stopImmediatePropagation();
      this.popup.querySelector('a')?.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.isOpen) this.open();
    const links = [...this.popup.querySelectorAll('a')];
    (event.key === 'ArrowUp' ? links.at(-1) : links[0])?.focus();
  };

  onOutsidePointer = event => {
    if (this.isOpen && !this.popup.contains(event.target) && !this.isMoreEvent(event)) this.close();
  };

  onFocus = event => {
    if (this.isOpen && !this.popup.contains(event.target) && !this.isMoreEvent(event)) this.close();
  };

  onPopupKey = event => {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }
    if (!this.popup.contains(event.target)) return;
    const links = [...this.popup.querySelectorAll('a')];
    if (!links.length) return;
    if (event.key === 'Tab') {
      if (event.shiftKey && document.activeElement === links[0]) {
        event.preventDefault();
        this.close(true);
      } else if (!event.shiftKey && document.activeElement === links.at(-1)) {
        this.close(true);
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let index = links.indexOf(document.activeElement);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = links.length - 1;
    else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
    links[index]?.focus();
  };

  onLinkClick = event => {
    if (event.target instanceof Element && event.target.closest('a')) this.close();
  };

  onResize = () => this.close();

  onScroll = event => {
    if (this.isOpen && !this.popup.contains(event.target)) this.position();
  };

  makeLink(source) {
    const anchor = document.createElement('a');
    anchor.href = source.getAttribute('href');
    anchor.textContent = source.querySelector('.menu-list__link-title')?.textContent?.trim() ||
      source.textContent.trim().replace(/\s+/g, ' ') || source.querySelector('img')?.alt || source.href;
    for (const name of ['target', 'rel', 'download', 'hreflang', 'aria-current']) {
      if (source.hasAttribute(name)) anchor.setAttribute(name, source.getAttribute(name));
    }
    return anchor;
  }

  open() {
    this.syncButton();
    const button = this.button;
    const overflowList = this.host.refs.overflowMenu;
    if (!button || !overflowList) return;
    const sourceItems = overflowList.shadowRoot?.querySelector('slot[name="overflow"]')?.assignedElements() ??
      [...overflowList.querySelectorAll(':scope > [slot="overflow"]')];
    const list = document.createElement('ul');
    for (const item of sourceItems) {
      const source = item.querySelector(':scope > a[href]');
      if (!source) continue;
      const row = document.createElement('li');
      row.append(this.makeLink(source));
      const seen = new Set([source.href]);
      const childList = document.createElement('ul');
      childList.className = 'tgm-more-sublist';
      for (const child of item.querySelectorAll('[ref="submenu[]"] a[href]')) {
        if (seen.has(child.href)) continue;
        seen.add(child.href);
        const childRow = document.createElement('li');
        childRow.append(this.makeLink(child));
        childList.append(childRow);
      }
      if (childList.childElementCount) row.append(childList);
      list.append(row);
    }
    if (!list.childElementCount) return;
    this.resetThemeMenu();
    this.popup.replaceChildren(list);
    const menuStyle = getComputedStyle(overflowList);
    const buttonStyle = getComputedStyle(button);
    const headerStyle = getComputedStyle(this.host.closest('header-component') ?? this.host);
    const color = menuStyle.getPropertyValue('--color-foreground').trim() || buttonStyle.color;
    const background = menuStyle.getPropertyValue('--color-background').trim() ||
      headerStyle.getPropertyValue('--color-background').trim() || '#ffffff';
    this.popup.style.setProperty('--tgm-menu-color', color);
    this.popup.style.setProperty('--tgm-menu-background', background);
    this.popup.style.fontFamily = buttonStyle.fontFamily;
    this.popup.hidden = false;
    if (typeof this.popup.showPopover === 'function') this.popup.showPopover();
    this.isOpen = true;
    button.setAttribute('aria-expanded', 'true');
    this.position();
  }

  position() {
    const button = this.button;
    if (!button) { this.close(); return; }
    const rect = button.getBoundingClientRect();
    const label = button.querySelector('.menu-list__link-title');
    let anchorRect = label?.getBoundingClientRect();
    if (!anchorRect?.height) {
      const range = document.createRange();
      range.selectNodeContents(button);
      anchorRect = range.getBoundingClientRect();
    }
    if (!anchorRect.height) anchorRect = rect;
    if (!button.getClientRects().length || rect.bottom <= 0 || rect.top >= window.innerHeight ||
      this.host.refs.overflowMenu?.shadowRoot?.querySelector('slot[name="more"]')?.hidden) {
      this.close();
      return;
    }
    const width = Math.min(250, window.innerWidth - 24);
    const below = window.innerHeight - anchorRect.bottom - 20;
    const above = anchorRect.top - 20;
    const placeAbove = below < 180 && above > below;
    this.popup.style.width = `${width}px`;
    this.popup.style.maxHeight = `${Math.max(44, Math.min(420, placeAbove ? above : below))}px`;
    this.popup.style.left = `${Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12))}px`;
    this.popup.style.top = `${placeAbove ? Math.max(12, anchorRect.top - this.popup.offsetHeight - 8) : anchorRect.bottom + 10}px`;
  }

  close(restoreFocus = false) {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (this.popup.matches(':popover-open')) this.popup.hidePopover();
    this.popup.hidden = true;
    this.button?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.button?.focus();
  }

  destroy() {
    this.close();
    this.abort.abort();
    this.observer.disconnect();
    this.popup.remove();
    this.currentButton?.removeAttribute('aria-controls');
  }

  static installStyles() {
    if (document.getElementById('tgm-more-dropdown-styles')) return;
    const style = document.createElement('style');
    style.id = 'tgm-more-dropdown-styles';
    style.textContent = `
      [data-tgm-more-trigger] { display:flex; flex-direction:row; align-items:center; gap:6px; }
      .tgm-more-chevron { display:block; width:7px; height:7px; border-right:1.5px solid currentColor; border-bottom:1.5px solid currentColor; transform:rotate(45deg); margin:0 12px 4px 0; }
      [data-tgm-more-trigger][aria-expanded="true"] .tgm-more-chevron { transform:rotate(225deg); margin-bottom:0; }
      .tgm-more-dropdown { position:fixed; inset:auto; margin:0; padding:8px; box-sizing:border-box; border:1px solid color-mix(in srgb,var(--tgm-menu-color) 16%,transparent); border-radius:10px; background:var(--tgm-menu-background); color:var(--tgm-menu-color); box-shadow:0 12px 32px rgb(0 0 0 / 15%); overflow-y:auto; overscroll-behavior:contain; z-index:10000; font-size:15px; line-height:1.4; text-align:start; }
      .tgm-more-dropdown[hidden] { display:none; }
      .tgm-more-dropdown ul { list-style:none; padding:0; margin:0; }
      .tgm-more-dropdown a { display:flex; align-items:center; min-height:44px; box-sizing:border-box; padding:10px 12px; border-radius:6px; color:inherit; text-decoration:none; white-space:normal; overflow-wrap:anywhere; font-weight:500; }
      .tgm-more-dropdown a:hover, .tgm-more-dropdown a:focus-visible { background:color-mix(in srgb,var(--tgm-menu-color) 7%,var(--tgm-menu-background)); }
      .tgm-more-dropdown a:focus-visible { outline:2px solid currentColor; outline-offset:-2px; }
      .tgm-more-dropdown a[aria-current="page"] { text-decoration:underline; text-underline-offset:4px; }
      .tgm-more-dropdown .tgm-more-sublist { padding-inline-start:14px; }
      .tgm-more-sublist a { font-size:14px; font-weight:400; }
    `;
    document.head.append(style);
  }
}


const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #moreDropdown;

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#moreDropdown?.destroy();
    this.#moreDropdown = new TgmMoreDropdown(this, () => this.#deactivate(this.#state.activeItem, true));

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    this.#moreDropdown?.destroy();
    this.#moreDropdown = undefined;
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    if (this.#moreDropdown?.isMoreEvent(event)) return;
    if (this.#moreDropdown?.isOpen) this.#moreDropdown.close();

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
